import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import config from '../Config';

/**
 * Pantalla completa que se muestra en lugar de la app cuando el backend
 * rechaza el acceso: VPN encendida o país bloqueado.
 *
 * Es solo para el usuario legítimo, que merece entender por qué no entra.
 * Lo que realmente impide el acceso es el middleware del backend: aunque
 * alguien saltee esta pantalla, la API no le devuelve datos.
 */

const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'pt', label: 'PT', name: 'Português' },
  { code: 'it', label: 'IT', name: 'Italiano' },
  { code: 'fr', label: 'FR', name: 'Français' },
];

const CONTENT = {
  en: {
    vpn_detected: {
      title: 'Turn off your VPN',
      paragraphs: [
        'We detected that you are browsing through a VPN or proxy. You cannot see models, packs, or any content on our site while it is enabled.',
        'Turn it off and reload the page to continue. If you did not know you were using a VPN, check your antivirus or your device settings — some enable one automatically.',
      ],
      loginHint: 'Already have a model or admin account?',
      loginButton: 'Log in',
    },
    country_blocked: {
      title: 'Not available in your region',
      paragraphs: ['This content is not available in your country.'],
    },
    button: 'Reload page',
  },
  es: {
    vpn_detected: {
      title: 'Apaga tu VPN',
      paragraphs: [
        'Detectamos que estás navegando a través de una VPN o proxy. No podés ver modelos, packs ni ningún contenido de nuestro sitio mientras esté activa.',
        'Apágala y recarga la página para continuar. Si no sabías que estabas usando una VPN, revisa tu antivirus o la configuración de tu dispositivo — algunos la activan automáticamente.',
      ],
      loginHint: '¿Ya tenés una cuenta de modelo o admin?',
      loginButton: 'Iniciar sesión',
    },
    country_blocked: {
      title: 'No disponible en tu región',
      paragraphs: ['Este contenido no está disponible en tu país.'],
    },
    button: 'Recargar página',
  },
  pt: {
    vpn_detected: {
      title: 'Desligue sua VPN',
      paragraphs: [
        'Detectamos que você está navegando através de uma VPN ou proxy. Você não pode ver modelos, packs ou qualquer conteúdo do nosso site enquanto ela estiver ativa.',
        'Desligue-a e recarregue a página para continuar. Se você não sabia que estava usando uma VPN, verifique seu antivírus ou as configurações do seu dispositivo — alguns a ativam automaticamente.',
      ],
      loginHint: 'Já tem uma conta de modelo ou admin?',
      loginButton: 'Entrar',
    },
    country_blocked: {
      title: 'Não disponível na sua região',
      paragraphs: ['Este conteúdo não está disponível no seu país.'],
    },
    button: 'Recarregar página',
  },
  it: {
    vpn_detected: {
      title: 'Disattiva la tua VPN',
      paragraphs: [
        'Abbiamo rilevato che stai navigando tramite una VPN o un proxy. Non puoi vedere modelle, pack o qualsiasi contenuto del nostro sito finché è attiva.',
        'Disattivala e ricarica la pagina per continuare. Se non sapevi di usare una VPN, controlla il tuo antivirus o le impostazioni del dispositivo — alcuni la attivano automaticamente.',
      ],
      loginHint: 'Hai già un account da modella o admin?',
      loginButton: 'Accedi',
    },
    country_blocked: {
      title: 'Non disponibile nella tua regione',
      paragraphs: ['Questo contenuto non è disponibile nel tuo paese.'],
    },
    button: 'Ricarica la pagina',
  },
  fr: {
    vpn_detected: {
      title: 'Désactivez votre VPN',
      paragraphs: [
        'Nous avons détecté que vous naviguez via un VPN ou un proxy. Vous ne pouvez pas voir les modèles, les packs ni aucun contenu de notre site tant qu\'il est activé.',
        "Désactivez-le et rechargez la page pour continuer. Si vous ne saviez pas que vous utilisiez un VPN, vérifiez votre antivirus ou les paramètres de votre appareil — certains l'activent automatiquement.",
      ],
      loginHint: 'Vous avez déjà un compte modèle ou admin ?',
      loginButton: 'Se connecter',
    },
    country_blocked: {
      title: 'Non disponible dans votre région',
      paragraphs: ["Ce contenu n'est pas disponible dans votre pays."],
    },
    button: 'Recharger la page',
  },
};

const STORAGE_KEY = 'accessGateLanguage';

/**
 * El registro y el login quedan exentos del bloqueo por VPN: quien llega por
 * un link de referido (yourpack.us/{codigo}/register) tiene que poder darse
 * de alta, y una modelo o admin con VPN encendida tiene que poder loguearse
 * para que el backend la deje pasar de ahí en más (ver EnforceGlobalCountryBlock).
 * Cubre /register, /{codigo}/register y /login.
 *
 * Esto solo salta el bloqueo por VPN. Si el país está bloqueado, el backend
 * responde bloqueado igual y la pantalla se muestra.
 */
const isVpnExemptPath = () =>
  /^\/([^/]+\/)?register\/?$/.test(window.location.pathname) ||
  /^\/login\/?$/.test(window.location.pathname);

/**
 * Si ya hay sesión no tiene sentido invitar a loguearse: esa invitación es para
 * el visitante anónimo que quiere entrar como modelo/admin, no para un cliente
 * ya logueado al que la VPN le sigue bloqueando el contenido.
 */
const isAuthenticated = () => {
  try {
    return localStorage.getItem('isAuth') === 'true';
  } catch {
    return false;
  }
};

/** Idioma elegido a mano si existe; si no, el del navegador; si no, inglés. */
const detectLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && CONTENT[saved]) return saved;
  } catch {
    // localStorage puede estar bloqueado (modo privado); seguimos con el navegador.
  }

  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || navigator.userLanguage || ''];

  for (const candidate of candidates) {
    const base = String(candidate).slice(0, 2).toLowerCase();
    if (CONTENT[base]) return base;
  }

  return 'en';
};

const AccessGate = ({ children }) => {
  const [status, setStatus] = useState('checking');
  const [verdict, setVerdict] = useState(null);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    setLanguage(detectLanguage());

    axios
      .get(`${config.REACT_APP_BASE_URL}/api/v1/access-check`)
      .then(({ data }) => {
        // En el alta y el login solo cortamos por país, nunca por VPN.
        if (data?.allowed === false && !(data.code === 'vpn_detected' && isVpnExemptPath())) {
          setVerdict(data);
          setStatus('blocked');
        } else {
          setStatus('allowed');
        }
      })
      // Ante un fallo de red no dejamos a nadie afuera: la app carga igual y,
      // si el bloqueo corresponde, el backend lo aplica en cada llamada.
      .catch(() => setStatus('allowed'));
  }, []);

  // Alguien que ya estaba adentro puede prender la VPN (o el país queda
  // bloqueado) después de este chequeo inicial. El interceptor de axios en
  // main.jsx dispara este evento ante cualquier 403 de bloqueo geográfico, y
  // acá se reacciona mostrando la pantalla completa en vez de dejar que cada
  // pantalla se las arregle sola con un listado vacío.
  useEffect(() => {
    const handleBlocked = (event) => {
      const detail = event.detail;

      // En /login y /register no se corta por VPN: todavía no sabemos si esta
      // persona es modelo o admin (eso recién se sabe cuando el login
      // responde), y cortar acá con un llamado de fondo (ej. notificaciones o
      // monedas de una sesión vieja en este mismo navegador) le taparía el
      // formulario antes de que llegue a loguearse. El bloqueo por país sigue
      // vigente igual: esa política no depende del rol.
      if (detail?.code === 'vpn_detected' && isVpnExemptPath()) return;

      setVerdict(detail);
      setStatus('blocked');
    };
    window.addEventListener('yp:access-blocked', handleBlocked);
    return () => window.removeEventListener('yp:access-blocked', handleBlocked);
  }, []);

  const chooseLanguage = (code) => {
    setLanguage(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Sin localStorage el cambio vale igual para esta visita.
    }
  };

  if (status === 'checking') return null;
  if (status === 'allowed') return children;

  const texts = CONTENT[language] || CONTENT.en;
  const screen = verdict?.code === 'vpn_detected' ? texts.vpn_detected : texts.country_blocked;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center bg-gray-900 overflow-y-auto p-4"
      translate="no"
    >
      <nav className="w-full max-w-md flex justify-center gap-1 py-4" aria-label="Language">
        {LANGUAGES.map(({ code, label, name }) => (
          <button
            key={code}
            type="button"
            onClick={() => chooseLanguage(code)}
            aria-label={name}
            aria-current={language === code}
            className={`px-3 py-1.5 rounded-[10px] text-sm font-semibold transition-colors ${
              language === code
                ? 'bg-pink-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-md w-full text-center pb-8">
          <h1 className="text-3xl font-semibold text-white mb-6">{screen.title}</h1>
          <div className="text-gray-300 mb-8 text-left">
            {screen.paragraphs.map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-8 rounded-xl transition-colors"
          >
            {texts.button}
          </button>

          {verdict?.code === 'vpn_detected' && !isAuthenticated() && (
            <div className="mt-6">
              <p className="text-gray-400 text-sm mb-3">{screen.loginHint}</p>
              <button
                type="button"
                // ?from=vpnBlock: así Login.jsx sabe que se llegó desde este
                // bloqueo y oculta el menú lateral (solo tiene sentido loguearse
                // acá para pasar el gate, no para navegar el resto del panel).
                onClick={() => window.location.assign('/login?from=vpnBlock')}
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-8 rounded-xl transition-colors"
              >
                {screen.loginButton}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AccessGate.propTypes = {
  children: PropTypes.node,
};

export default AccessGate;
